import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../hooks/useSEO";

const STATUS_STYLES = {
  pending_payment: "border-yellow-500/30 text-yellow-400",
  paid: "border-[#17B890]/30 text-[#17B890]",
  delivered: "border-[#D4AF37]/40 text-[#D4AF37]",
};

const STATUS_LABELS = {
  pending_payment: "Awaiting payment",
  paid: "Being prepared",
  delivered: "Report ready",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [downloading, setDownloading] = useState(null);
  useSEO("My Reports | NAKSRA", "View your NAKSRA orders and download your completed Vedic astrology reports.");

  useEffect(() => {
    api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  const download = async (order) => {
    setDownloading(order.id);
    try {
      const res = await api.get(`/orders/${order.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NAKSRA-${order.service_id}-${order.id.slice(0, 8)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-16" data-testid="account-page">
        <p className="text-xs font-medium uppercase tracking-[0.35em] text-[#D4AF37]">My Account</p>
        <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight">Namaste, {user?.name}</h1>
        <p className="mt-2 text-sm text-[#A1A1AA]">{user?.email}</p>

        <div className="mt-12" data-testid="orders-list">
          {orders === null ? (
            <p className="text-[#A1A1AA]">Loading your orders…</p>
          ) : orders.length === 0 ? (
            <div className="rounded-lg border border-white/10 bg-[#0C0C10] p-12 text-center" data-testid="orders-empty">
              <FileText className="mx-auto h-8 w-8 text-[#D4AF37]" />
              <h2 className="mt-4 font-heading text-2xl font-medium">No reports yet</h2>
              <p className="mt-2 text-sm text-[#A1A1AA]">Your purchased reports will appear here, ready to download.</p>
              <Link to="/services" data-testid="empty-browse-cta" className="mt-6 inline-block rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D]">
                Browse reports
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="flex flex-col gap-4 rounded-lg border border-white/10 bg-[#0C0C10] p-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`order-card-${o.id}`}>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="font-heading text-2xl font-medium">{o.service_name}</h2>
                      <span className={`rounded-full border px-3 py-0.5 text-xs ${STATUS_STYLES[o.status] || "border-white/20 text-[#A1A1AA]"}`} data-testid={`order-status-${o.id}`}>
                        {STATUS_LABELS[o.status] || o.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-[#A1A1AA]">
                      For {o.full_name} · {o.birth_date} {o.birth_time} · {o.birth_place}
                    </p>
                    <p className="mt-1 text-xs text-[#A1A1AA]">Order {o.id.slice(0, 8).toUpperCase()} · ${o.price.toFixed(2)} · Placed {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="shrink-0">
                    {o.status === "delivered" ? (
                      <button
                        onClick={() => download(o)}
                        disabled={downloading === o.id}
                        data-testid={`download-report-btn-${o.id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98] disabled:opacity-60"
                      >
                        {downloading === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Download report
                      </button>
                    ) : o.status === "paid" ? (
                      <span className="text-sm text-[#A1A1AA]">Our astrologer is verifying your report — you'll be emailed when it's ready.</span>
                    ) : (
                      <span className="text-sm text-[#A1A1AA]">Payment not completed.</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
